import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FaUsers, 
  FaUser, 
  FaGraduationCap, 
  FaCalendarAlt,
  FaKey,
  FaSearch,
  FaEye,
  FaSpinner
} from 'react-icons/fa';
import axios from 'axios';
import config from '../config/api';

const StudentsList = () => {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedStudent, setExpandedStudent] = useState(null);

  useEffect(() => {
    const fetchStudents = async () => {
      try {
        const response = await axios.get(`${config.apiURL}/teacher/students`);
        setStudents(response.data.students);
      } catch (err) {
        setError('Erro ao carregar lista de alunos');
        console.error('Erro ao carregar alunos:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchStudents();
  }, []);

  const filteredStudents = students.filter(student =>
    student.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.grade.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (student.guardian && student.guardian.name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const toggleExpand = (studentId) => {
    setExpandedStudent(expandedStudent === studentId ? null : studentId);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Não informado';
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const getAge = (birthDate) => {
    if (!birthDate) return 'Não informado';
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    
    return `${age} anos`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center items-center h-64">
            <div className="text-center">
              <FaSpinner className="animate-spin h-12 w-12 text-blue-600 mx-auto mb-4" />
              <p className="text-gray-600">Carregando alunos...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-red-600 mb-4">{error}</p>
            <button 
              onClick={() => window.location.reload()}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
              Tentar novamente
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center space-x-3">
            <FaUsers className="text-blue-600" />
            <span>Meus Alunos</span>
          </h1>
          <p className="mt-2 text-gray-600">
            Gerencie e acompanhe o progresso de todos os seus alunos
          </p>
        </div>

        {/* Search and Filter */}
        <div className="mb-6 bg-white rounded-lg shadow-md p-4">
          <div className="flex items-center space-x-4">
            <div className="flex-1 relative">
              <FaSearch className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Pesquisar por nome do aluno, série ou responsável..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="text-sm text-gray-600">
              {filteredStudents.length} aluno(s) encontrado(s)
            </div>
          </div>
        </div>

        {/* Students List */}
        {filteredStudents.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <FaUsers className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {searchTerm ? 'Nenhum aluno encontrado' : 'Nenhum aluno cadastrado'}
            </h3>
            <p className="text-gray-600 mb-4">
              {searchTerm 
                ? 'Tente ajustar os termos de pesquisa'
                : 'Cadastre seus primeiros alunos para começar a usar o sistema'
              }
            </p>
            {!searchTerm && (
              <button
                onClick={() => navigate('/create-student')}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
              >
                Cadastrar Primeiro Aluno
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {filteredStudents.map((student) => (
              <div key={student.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                <div className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="bg-blue-100 rounded-full p-3">
                        <FaUser className="h-6 w-6 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          {student.name}
                        </h3>
                        <div className="flex items-center space-x-4 text-sm text-gray-600">
                          <div className="flex items-center space-x-1">
                            <FaGraduationCap className="h-4 w-4" />
                            <span>{student.grade}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <FaCalendarAlt className="h-4 w-4" />
                            <span>{getAge(student.birth_date)}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <FaKey className="h-4 w-4" />
                            <span>{student.access_key}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => toggleExpand(student.id)}
                      className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-md transition-colors flex items-center space-x-2"
                    >
                      <FaEye className="h-4 w-4" />
                      <span>{expandedStudent === student.id ? 'Ocultar' : 'Ver Detalhes'}</span>
                    </button>
                  </div>

                  {expandedStudent === student.id && (
                    <div className="mt-6 pt-6 border-t border-gray-200">
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Informações do Aluno */}
                        <div>
                          <h4 className="text-md font-semibold text-gray-900 mb-3 flex items-center space-x-2">
                            <FaUser className="h-4 w-4 text-blue-600" />
                            <span>Informações do Aluno</span>
                          </h4>
                          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                            <div className="flex justify-between">
                              <span className="font-medium text-gray-700">Nome:</span>
                              <span className="text-gray-900">{student.name}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="font-medium text-gray-700">Série:</span>
                              <span className="text-gray-900">{student.grade}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="font-medium text-gray-700">Nascimento:</span>
                              <span className="text-gray-900">{formatDate(student.birth_date)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="font-medium text-gray-700">Idade:</span>
                              <span className="text-gray-900">{getAge(student.birth_date)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="font-medium text-gray-700">Chave de Acesso:</span>
                              <span className="text-gray-900 font-mono">{student.access_key}</span>
                            </div>
                          </div>
                        </div>

                        {/* Informações do Responsável */}
                        {student.guardian && (
                          <div>
                            <h4 className="text-md font-semibold text-gray-900 mb-3 flex items-center space-x-2">
                              <FaUsers className="h-4 w-4 text-green-600" />
                              <span>Informações do Responsável</span>
                            </h4>
                            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                              <div className="flex justify-between">
                                <span className="font-medium text-gray-700">Nome:</span>
                                <span className="text-gray-900">{student.guardian.name}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="font-medium text-gray-700">Email:</span>
                                <span className="text-gray-900">{student.guardian.email}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="font-medium text-gray-700">Telefone:</span>
                                <span className="text-gray-900">{student.guardian.phone}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="font-medium text-gray-700">Chave de Acesso:</span>
                                <span className="text-gray-900 font-mono">{student.guardian.access_key}</span>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Ações */}
                      <div className="mt-6 pt-4 border-t border-gray-200">
                        <div className="flex space-x-3">
                          <button 
                            onClick={() => navigate(`/student/${student.id}/progress`)}
                            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
                          >
                            Ver Progresso
                          </button>
                          <button 
                            onClick={() => navigate(`/student/${student.id}/homeworks`)}
                            className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors"
                          >
                            Ver Lições
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentsList; 